import { useState, useCallback, useEffect, useRef } from 'react'

import { Icon } from '@circlefin/components/lib/Icon'
import classNames from 'classnames'

import { CollapsibleGroupItem } from './CollapsibleGroupItem'

import '@circlefin/components/lib/Sidenav/Sidenav.CollapsibleGroup/Sidenav.CollapsibleGroup.css'

interface SubComponents {
  Item: typeof CollapsibleGroupItem
}

export interface CollapsibleGroupProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Default collapsed state.
   */
  defaultCollapsedState?: boolean
  /**
   * Group name.
   */
  groupName: string
  /**
   * Custom style for group items wrapper.
   */
  className?: string
  /**
   * When provided, the header navigates to the href as defined in onNavigate.
   */
  href?: string
  /**
   * Mark the header as active.
   */
  active?: boolean
  /**
   * Custom navigation handler for use when href is provided.
   */
  onNavigate?: (href: string) => void
}

export const CollapsibleGroup: React.FC<CollapsibleGroupProps> &
  SubComponents = ({
  defaultCollapsedState = true,
  groupName,
  className,
  href,
  active = false,
  onNavigate,
  children,
  ...props
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsedState)
  const [hasUserInteracted, setHasUserInteracted] = useState(false)

  // Manage collapsed state based on various conditions
  const prevActive = useRef(active)
  useEffect(() => {
    // Handle case where user has interacted with the group, but it is currently collapsed
    if (!prevActive.current && active && isCollapsed) {
      setIsCollapsed(false)
      prevActive.current = active
      return
    }

    // Handle case where user navigates directly to this item
    if (!hasUserInteracted && !defaultCollapsedState && isCollapsed) {
      setIsCollapsed(false)
    }

    prevActive.current = active
  }, [defaultCollapsedState, hasUserInteracted, isCollapsed, active])

  const toggleCollapse = useCallback(() => {
    setIsCollapsed(!isCollapsed)
    setHasUserInteracted(true)
  }, [isCollapsed])

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!href) {
        // Non-navigable group - just toggle
        toggleCollapse()
        return
      }

      if (active) {
        // Active navigable group - just toggle, don't navigate
        event.preventDefault()
        toggleCollapse()
        return
      }

      if (onNavigate) {
        // Inactive navigable group - handle navigation
        event.preventDefault()
        onNavigate(href)
      }
    },
    [href, active, toggleCollapse, onNavigate],
  )

  return (
    <div {...props}>
      <button
        className={classNames('cb-sidenav-collapsible-group', {
          'item-link': !!href || active,
          active: active,
        })}
        onClick={handleClick}
        type="button"
      >
        <span className={classNames('inner')}>
          <span className={classNames('label')}>{groupName}</span>
          <Icon
            className={classNames('icon', { reverse: isCollapsed })}
            name="ChevronUpSolid"
            size={20}
          />
        </span>
      </button>
      <span
        className={classNames('cb-group-items', className, {
          collapsed: isCollapsed,
        })}
        data-testid="collapsible-group-items"
      >
        {children}
      </span>
    </div>
  )
}

CollapsibleGroup.displayName = 'CollapsibleGroup'
CollapsibleGroup.Item = CollapsibleGroupItem
